import { z } from 'zod'

import { apiStateSchema, apiValueSchema, type ApiState } from './common'

export const stationSchema = z.object({
  id: z.string(),
  /** Код трассы/объекта, например «М36». */
  code: z.string(),
  name: z.string(),
  city: z.string(),
  address: z.string(),
})
export type Station = z.infer<typeof stationSchema>

/**
 * Топливная колонка — как в internal_api.ColumnResponse бэкенда. С 17.09.2026 счётчики — объекты
 * Value (ТЗ п. 8): число есть только при status = ok, чтобы колонку без показаний нельзя было
 * показать как «0 авто».
 */
export const columnSchema = z.object({
  column_number: z.number(),
  /**
   * Автомобилей у колонки сейчас. unavailable — камера молчит или снимок старше
   * COLUMN_STATUS_STALE_AFTER, no_data — по колонке ничего не приходило.
   */
  vehicles: apiValueSchema,
  /** Последнее показание со счётчиком — отличает камеру, сломавшуюся минуту назад, от никогда не работавшей. */
  last_available: apiValueSchema.optional(),
})
export type Column = z.infer<typeof columnSchema>

/** GET /columns. updated_at отсутствует, пока не пришёл первый снимок. */
export const columnsSchema = z.object({
  columns: z.array(columnSchema),
  updated_at: z.string().optional(),
})
export type Columns = z.infer<typeof columnsSchema>

/**
 * Статус кассы для плитки: present («На месте») и absent («Отсутствует») — значение presence при
 * status = ok; unavailable — камера молчит или снимок старше CASHIER_STATUS_STALE_AFTER ≈ 11 мин
 * (updated_at при этом сохраняется); no_data — по кассе ничего не приходило.
 */
export const CASH_DESK_STATUSES = ['present', 'absent', 'unavailable', 'no_data'] as const
export type CashDeskStatus = (typeof CASH_DESK_STATUSES)[number]

/** Касса — как в internal_api.CashDeskResponse: присутствие кассира — объект State (с 17.09.2026). */
export const cashDeskSchema = z.object({
  cash_desk_number: z.number(),
  presence: apiStateSchema,
})
export type CashDesk = z.infer<typeof cashDeskSchema>

/**
 * Статус кассы из presence. Незнакомое значение при ok считаем unavailable, чтобы не показать
 * «На месте» по ошибке (ТЗ п. 8).
 */
export const getCashDeskStatus = (presence: ApiState | undefined): CashDeskStatus => {
  if (!presence) return 'unavailable'
  if (presence.status !== 'ok') return presence.status
  return presence.value === 'present' || presence.value === 'absent'
    ? presence.value
    : 'unavailable'
}

/** GET /cashiers. Всегда две кассы; updated_at отсутствует, пока не пришёл первый снимок. */
export const cashiersSchema = z.object({
  cash_desks: z.array(cashDeskSchema),
  updated_at: z.string().optional(),
})
export type Cashiers = z.infer<typeof cashiersSchema>
